import type { TextRenderOutput } from '@/framework/services/text-render';
import { createSVGElement } from '@/utils/svg-kit/element';
import type { BoundingBox } from '@/version-types';
import { applyTextLayoutFlip } from '../utils/bbox';

type Options = {
    flipH: boolean;
    flipV: boolean;
    opacity: number;
    /** 完全空文本没有 Renderer output，使用宿主尺寸生成透明资源 */
    width: number;
    height: number;
};

type Result = {
    svg: SVGSVGElement;
    /**
     * 相对文字图层左上角的导出范围，供宿主应用舞台位置与旋转
     *
     * 与 output.exportRenderBox 的 snapshot 局部坐标不同，此处已减去 layoutBox 的左上角。
     * SVG 内部则已通过 exportOffset 将内容归零，两者分别负责图片在舞台上的位置和图片内部的绘制位置。
     */
    renderBox: BoundingBox;
};

/**
 * 使用当前 Renderer output 构建独立导出 SVG，合法空白生成透明资源
 *
 * 图层合并（EdgelessTextLayer.getResult）和整画布出图（getResultCanvasAsync）都将返回的
 * renderBox 当作相对图层左上角的范围，因此在此统一完成 snapshot 坐标到图层坐标的转换。
 * 转换必须使用本次 output 的 layoutBox；运行时 drawable 可能仍保留旧布局，不能用它定位新导出的图片。
 *
 * @param output - 已等待字体和素材完成的当前文字输出；null 表示成功的 empty 状态。
 * @param options - 宿主负责的 outer flip 与透明度。
 * @returns 可供 SVG 序列化和 Canvas 定位共同消费的导出结果。
 */
export const createExportSVGResult = (output: TextRenderOutput | null, options: Options): Result => {
    const { flipH, flipV, opacity } = options;
    const sourceLayoutBox = output?.layoutBox ?? { left: 0, top: 0, width: options.width, height: options.height };
    const sourceRenderBox = output?.exportRenderBox ?? sourceLayoutBox;

    // 空白文字可以有有效行几何，但正文宽度/高度为 0；旧检查将其误判为导出失败，
    // 阻断整画布下载、我的空间保存和单图层导出。正文为空也可能附带可见背景或列表标记，
    // 因此不能按 trim()、字形数量或正文尺寸丢弃整个 SVG，必须保留 Renderer 的最终视觉输出。
    // 对合法零尺寸只补到 Canvas/SVG 可用的最小 1px；empty 没有输出时按宿主框生成透明 SVG。
    // 字体/素材失败仍由上游抛错；负数、NaN、Infinity 不属于合法空白，不能伪装成成功图片。
    if ([sourceRenderBox.width, sourceRenderBox.height].some((size) => !Number.isFinite(size) || size < 0)) {
        throw new Error('Export layout has invalid dimensions.');
    }

    // Renderer 的 SVG 仍供 runtime/Preview 复用，导出只能修改克隆节点。
    const svg = output ? (output.svg.cloneNode(true) as SVGSVGElement) : createSVGElement('svg');
    const { renderBox, layoutBox, offset } = applyTextLayoutFlip(
        {
            layoutBox: sourceLayoutBox,
            renderBox: {
                ...sourceRenderBox,
                width: sourceRenderBox.width === 0 ? 1 : sourceRenderBox.width,
                height: sourceRenderBox.height === 0 ? 1 : sourceRenderBox.height,
            },
            offset: output?.exportOffset ?? { x: 0, y: 0 },
        },
        flipH,
        flipV,
    );

    const group = createSVGElement('g');
    if (offset.x !== 0 || offset.y !== 0) {
        group.setAttribute('transform', `translate(${offset.x} ${offset.y})`);
    }

    if (opacity < 1) {
        group.setAttribute('opacity', String(Math.max(0, opacity)));
    }

    let content = group;
    if (flipH || flipV) {
        const centerX = layoutBox.left + layoutBox.width / 2;
        const centerY = layoutBox.top + layoutBox.height / 2;
        const flipGroup = createSVGElement('g');
        flipGroup.setAttribute(
            'transform',
            [
                `translate(${centerX} ${centerY})`,
                `scale(${flipH ? -1 : 1} ${flipV ? -1 : 1})`,
                `translate(${-centerX} ${-centerY})`,
            ].join(' '),
        );
        group.appendChild(flipGroup);
        content = flipGroup;
    }

    while (svg.firstChild) {
        content.appendChild(svg.firstChild);
    }
    svg.appendChild(group);
    svg.setAttribute('width', String(renderBox.width));
    svg.setAttribute('height', String(renderBox.height));
    svg.setAttribute('viewBox', `0 0 ${renderBox.width} ${renderBox.height}`);

    return {
        svg,
        renderBox: {
            ...renderBox,
            // 横排 layoutBox.left 为 0；vertical-rl 以右侧为原点，layoutBox.left 为负的排版宽度。
            // 例如宽 180 的竖排文字，导出左边界为 -170，表示距图层左边缘 10，而不是向左偏移 170。
            // 若直接返回 snapshot 坐标，每层会向左错移自身排版宽度；两层宽度不同时，合并后的间距也会改变。
            // 此处只转换给宿主定位的边界；上面的 SVG 平移和翻转仍须使用 snapshot 坐标，不能提前修改 renderBox。
            left: renderBox.left - layoutBox.left,
            top: renderBox.top - layoutBox.top,
        },
    };
};
